const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
export const SCHOOL_HOME_PAGE_API = `${API_BASE}/api/public/school-home-page`;

export const SCHOOL_HOME_PAGE_STAT_LABELS = {
  totalStudents: 'Total Students',
  attendanceRate: 'Attendance Rate',
  activeUsers: 'Active Users',
  uptimeGuarantee: 'Uptime Guarantee',
  totalSubjects: 'Total Subjects',
  totalReports: 'Total Reports',
  totalSchools: 'Total Schools',
};

export const buildStatsMap = (stats) => {
  if (!stats) return {};

  if (Array.isArray(stats)) {
    return stats.reduce((acc, item) => {
      const label = item?.label || item?.title || item?.name;
      const value = item?.value ?? item?.display ?? item?.count;
      if (label != null && value != null && value !== '') {
        acc[String(label).trim()] = String(value);
      }
      return acc;
    }, {});
  }

  if (typeof stats === 'object') {
    return Object.entries(stats).reduce((acc, [key, item]) => {
      const label = SCHOOL_HOME_PAGE_STAT_LABELS[key] || item?.label || item?.title || key;
      const value = typeof item === 'object' && item !== null
        ? item?.value ?? item?.display ?? item?.count
        : item;
      if (label && value != null && value !== '') {
        acc[String(label).trim()] = String(value);
      }
      return acc;
    }, {});
  }

  return {};
};

export const getStatFromMap = (statsMap, label) => statsMap[label] || '—';

const MAX_PRICING_PLANS = 100;
const MAX_PLAN_FEATURES = 50;

const displayedSortAmount = (plan) => {
  const explicit = Number(plan?.priceAmount);
  if (Number.isFinite(explicit)) return explicit;
  const match = String(plan?.price || '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const sortPricingPlansByPriceDesc = (plans) => {
  if (!Array.isArray(plans)) return [];
  return plans.slice().sort((a, b) => {
    const aAmount = displayedSortAmount(a);
    const bAmount = displayedSortAmount(b);
    if (aAmount == null && bAmount == null) return 0;
    if (aAmount == null) return 1;
    if (bAmount == null) return -1;
    return bAmount - aAmount;
  });
};

export const normalizePricingPlans = (plans) => {
  if (!Array.isArray(plans)) return [];

  const normalized = plans.slice(0, MAX_PRICING_PLANS).map((plan) => {
    if (!plan || typeof plan !== 'object') return null;

    const title = String(plan.title || '').trim();
    const price = String(plan.price || '').trim();
    if (!title || !price) return null;

    const features = Array.isArray(plan.features)
      ? plan.features
        .map((feature) => String(feature || '').trim())
        .filter(Boolean)
        .slice(0, MAX_PLAN_FEATURES)
      : [];

    const id = Number(plan.id);
    const priceAmount = displayedSortAmount(plan);

    return {
      id: Number.isInteger(id) && id > 0 ? id : title,
      title,
      price,
      priceAmount,
      priceDetail: plan.priceDetail ? String(plan.priceDetail).trim() : null,
      pricePeriod: plan.pricePeriod ? String(plan.pricePeriod).trim() : null,
      description: String(plan.description || '').trim(),
      features,
      ctaLabel: String(plan.ctaLabel || plan.cta || plan.label || 'Request Demo').trim(),
      popular: Boolean(plan.popular),
      setupFee: plan.setupFee ? String(plan.setupFee).trim() : null,
      trial: plan.trial ? String(plan.trial).trim() : null,
    };
  }).filter(Boolean);

  return sortPricingPlansByPriceDesc(normalized);
};

export const fetchSchoolHomePageData = async (signal) => {
  const response = await fetch(SCHOOL_HOME_PAGE_API, { signal });

  if (!response.ok) {
    throw new Error(`School home page API failed (${response.status})`);
  }

  const result = await response.json();
  const content = result?.success ? result?.data?.content : null;
  const pricingUnavailable = content?.pricing?.unavailable === true;

  return {
    statsMap: content?.stats ? buildStatsMap(content.stats) : {},
    pricingPlans: pricingUnavailable ? [] : normalizePricingPlans(content?.pricing?.plans),
    pricingUnavailable,
  };
};

export const fetchSchoolHomePageStats = async (signal) => {
  const { statsMap } = await fetchSchoolHomePageData(signal);
  return statsMap;
};
